import type { UtilityToolDefinition } from '@saas-pages/shared/public/platform-legacy'
import { ruTranslations, type Translations } from '@/shared/i18n/ru'

export type ToolKey =
  | 'textCounter'
  | 'textCase'
  | 'dedupeLines'
  | 'textCleanup'
  | 'transliteration'
  | 'fancyText'
  | 'seoLimits'
  | 'passwordGenerator'

export type ToolStatus = 'available' | 'coming-soon'
export type ToolCategory = 'count' | 'clean' | 'format' | 'generate' | 'seo'

export interface ToolDefinition extends UtilityToolDefinition<ToolKey, ToolCategory> {}

export const CATEGORY_ACCENT: Record<ToolCategory, string> = {
  count:
    'border-lime-300 bg-lime-50 text-lime-900 dark:border-lime-700 dark:bg-lime-950/45 dark:text-lime-100',
  clean:
    'border-cyan-300 bg-cyan-50 text-cyan-900 dark:border-cyan-700 dark:bg-cyan-950/45 dark:text-cyan-100',
  format:
    'border-violet-300 bg-violet-50 text-violet-900 dark:border-violet-700 dark:bg-violet-950/45 dark:text-violet-100',
  generate:
    'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-700 dark:bg-emerald-950/45 dark:text-emerald-100',
  seo:
    'border-rose-300 bg-rose-50 text-rose-950 dark:border-rose-700 dark:bg-rose-950/45 dark:text-rose-100',
}

export function buildCategoryLabel(t: Translations): Record<ToolCategory, string> {
  return {
    count: t.categories.count,
    clean: t.categories.clean,
    format: t.categories.format,
    generate: t.categories.generate,
    seo: t.categories.seo,
  }
}

export function buildTools(t: Translations): readonly ToolDefinition[] {
  return [
    {
      key: 'textCounter',
      path: '/schetchik-simvolov',
      analyticsKey: 'text-counter',
      category: 'count',
      title: t.tools.textCounter.title,
      shortDescription: t.tools.textCounter.shortDescription,
      longDescription: t.tools.textCounter.longDescription,
      icon: '#',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/schetchik-simvolov',
        title: t.tools.textCounter.seoTitle,
        description: t.tools.textCounter.seoDescription,
        changefreq: 'monthly',
        priority: 1,
      },
      useCases: t.tools.textCounter.useCases,
      howTo: t.tools.textCounter.howTo,
      faq: t.tools.textCounter.faq,
    },
    {
      key: 'textCase',
      path: '/registr-teksta',
      analyticsKey: 'text-case',
      category: 'format',
      title: t.tools.textCase.title,
      shortDescription: t.tools.textCase.shortDescription,
      longDescription: t.tools.textCase.longDescription,
      icon: 'Aa',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/registr-teksta',
        title: t.tools.textCase.seoTitle,
        description: t.tools.textCase.seoDescription,
        changefreq: 'monthly',
        priority: 0.7,
      },
      useCases: t.tools.textCase.useCases,
      howTo: t.tools.textCase.howTo,
      faq: t.tools.textCase.faq,
    },
    {
      key: 'dedupeLines',
      path: '/udalit-dubli-strok',
      analyticsKey: 'dedupe-lines',
      category: 'clean',
      title: t.tools.dedupeLines.title,
      shortDescription: t.tools.dedupeLines.shortDescription,
      longDescription: t.tools.dedupeLines.longDescription,
      icon: '1x',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/udalit-dubli-strok',
        title: t.tools.dedupeLines.seoTitle,
        description: t.tools.dedupeLines.seoDescription,
        changefreq: 'monthly',
        priority: 0.7,
      },
      useCases: t.tools.dedupeLines.useCases,
      relatedTools: ['textCleanup', 'textCase', 'textCounter'],
      howTo: t.tools.dedupeLines.howTo,
      faq: t.tools.dedupeLines.faq,
    },
    {
      key: 'textCleanup',
      path: '/ochistit-tekst',
      analyticsKey: 'text-cleanup',
      category: 'clean',
      title: t.tools.textCleanup.title,
      shortDescription: t.tools.textCleanup.shortDescription,
      longDescription: t.tools.textCleanup.longDescription,
      icon: '⌁',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/ochistit-tekst',
        title: t.tools.textCleanup.seoTitle,
        description: t.tools.textCleanup.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.textCleanup.useCases,
      relatedTools: ['dedupeLines', 'textCase', 'textCounter'],
      howTo: t.tools.textCleanup.howTo,
      faq: t.tools.textCleanup.faq,
    },
    {
      key: 'transliteration',
      path: '/transliteraciya',
      analyticsKey: 'transliteration',
      category: 'format',
      title: t.tools.transliteration.title,
      shortDescription: t.tools.transliteration.shortDescription,
      longDescription: t.tools.transliteration.longDescription,
      icon: 'А→A',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/transliteraciya',
        title: t.tools.transliteration.seoTitle,
        description: t.tools.transliteration.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.transliteration.useCases,
      relatedTools: ['textCase', 'seoLimits'],
      howTo: t.tools.transliteration.howTo,
      faq: t.tools.transliteration.faq,
    },
    {
      key: 'fancyText',
      path: '/krasivyy-tekst',
      analyticsKey: 'fancy-text',
      category: 'format',
      title: t.tools.fancyText.title,
      shortDescription: t.tools.fancyText.shortDescription,
      longDescription: t.tools.fancyText.longDescription,
      icon: '𝑨',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/krasivyy-tekst',
        title: t.tools.fancyText.seoTitle,
        description: t.tools.fancyText.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.fancyText.useCases,
      relatedTools: ['textCase', 'transliteration', 'textCounter'],
      howTo: t.tools.fancyText.howTo,
      faq: t.tools.fancyText.faq,
    },
    {
      key: 'passwordGenerator',
      path: '/generator-parolej',
      analyticsKey: 'password-generator',
      category: 'generate',
      title: t.tools.passwordGenerator.title,
      shortDescription: t.tools.passwordGenerator.shortDescription,
      longDescription: t.tools.passwordGenerator.longDescription,
      icon: '***',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/generator-parolej',
        title: t.tools.passwordGenerator.seoTitle,
        description: t.tools.passwordGenerator.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.passwordGenerator.useCases,
      howTo: t.tools.passwordGenerator.howTo,
      faq: t.tools.passwordGenerator.faq,
    },
    {
      key: 'seoLimits',
      path: '/seo-limity',
      analyticsKey: 'seo-limits',
      category: 'seo',
      title: t.tools.seoLimits.title,
      shortDescription: t.tools.seoLimits.shortDescription,
      longDescription: t.tools.seoLimits.longDescription,
      icon: 'SEO',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/seo-limity',
        title: t.tools.seoLimits.seoTitle,
        description: t.tools.seoLimits.seoDescription,
        changefreq: 'monthly',
        priority: 0.7,
      },
      useCases: t.tools.seoLimits.useCases,
      howTo: t.tools.seoLimits.howTo,
      faq: t.tools.seoLimits.faq,
    },
  ]
}

export const TOOLS = buildTools(ruTranslations)

export const AVAILABLE_TOOLS = TOOLS.filter((tool) => tool.status === 'available')

export function getTool(
  key: ToolKey,
  tools: readonly ToolDefinition[] = TOOLS,
): ToolDefinition {
  const tool = tools.find((item) => item.key === key)
  if (!tool) {
    throw new Error(`[txtify tools] Unknown tool key: ${key}`)
  }
  return tool
}

export function relatedTools(
  current: ToolKey,
  tools: readonly ToolDefinition[] = TOOLS,
  limit = 3,
): ToolDefinition[] {
  const currentTool = getTool(current, tools)
  const explicit = currentTool.relatedTools

  if (explicit && explicit.length > 0) {
    return explicit
      .map((key) => tools.find((tool) => tool.key === key))
      .filter((tool): tool is ToolDefinition => Boolean(tool))
      .slice(0, limit)
  }

  return tools
    .filter((tool) => tool.key !== current && tool.category === currentTool.category)
    .concat(tools.filter((tool) => tool.key !== current && tool.category !== currentTool.category))
    .slice(0, limit)
}
